import { Divider, Stack } from '@mui/material';
import { useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LassieMiniParams } from '@thive/fddm-types';
import { degToRad, radToDeg } from 'three/src/math/MathUtils.js';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { WorkspaceMode } from '@/modules/common/types/general';
import { NumberInput } from '@/modules/common/components/inputs';
import { NumUtils } from '@/modules/common/utils/NumUtils';
import { computeRectanglesBoundingBoxExtent } from '@/modules/common/helpers/boundingBox';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useReadSelectedTemplates } from '@/modules/floorplanDesign/hooks';
import {
  areasToFlatCollection,
  hasNoTemplates,
  isHighwayArea,
} from '@/modules/floorplanDesign/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { TemplateDimensions } from './TemplateDimensions';

function LassieMiniTemplateParametersComponent() {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = TpsServiceFactory.getInstance().createTpsService();
  const selectedGroups = useReadSelectedTemplates();

  const lassieMiniGroup = useMemo(() => {
    if (hasNoTemplates(selectedGroups)) return;

    const group = selectedGroups.lassieMinis[0];
    if (!group) return;

    return group;
  }, [selectedGroups]);

  const newGenerationParams = useMemo(() => {
    if (!lassieMiniGroup) return;

    return lassieMiniGroup.params;
  }, [lassieMiniGroup]);

  const dimensions = useFloorplanDesignStore((state) => {
    if (!lassieMiniGroup) return;

    const areasInGroupIds = lassieMiniGroup.group.areaIds;
    const areasInGroups = areasToFlatCollection(state.getAreasByIds(areasInGroupIds));

    const rectangles = areasInGroups.flatMap((item) =>
      isHighwayArea(item) ? item.segments.flatMap(RectangleUtils.fromSegment) : [item.rectangle],
    );

    if (rectangles.length === 0) return null;

    const box = computeRectanglesBoundingBoxExtent(rectangles);
    return { x: box.x, y: box.y, length: box.width, width: box.height };
  });

  const regenerate = useCallback(
    async (params?: Partial<LassieMiniParams>) => {
      if (!dimensions || !newGenerationParams || !lassieMiniGroup) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      await tpsSvc.regenerateLassieMiniTemplate({
        ...lassieMiniGroup,
        params: { ...newGenerationParams, ...params },
      });

      loadingSvc.hideLoadContainer();
    },
    [dimensions, newGenerationParams, lassieMiniGroup, tpsSvc, loadingSvc],
  );

  const onNumAislesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassieMini.numAisles', newValue);
      regenerate({ numAisles: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumBaysChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassieMini.numBays', newValue);
      regenerate({ numBays: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumShelvesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassieMini.numShelves', newValue);
      regenerate({ numShelves: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onCenterXChange = useCallback(
    (newValue: number) => {
      regenerate({ centerX: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onCenterYChange = useCallback(
    (newValue: number) => {
      regenerate({ centerY: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      regenerate({ angle: degToRad(newValue) });
    },
    [regenerate],
  );

  if (!newGenerationParams || !dimensions) return null;

  return (
    <Stack spacing={1} divider={<Divider />}>
      <TemplateDimensions
        x={newGenerationParams.centerX}
        y={newGenerationParams.centerY}
        length={dimensions.length}
        width={dimensions.width}
        angle={radToDeg(newGenerationParams.angle)}
        onXChange={onCenterXChange}
        onYChange={onCenterYChange}
        onAngleChange={onAngleChange}
      />
      <Accordion
        title={t('interface:properties.template_title.lassie_mini', 'Lassie Mini')}
        unmountOnExit
        defaultExpanded
        sx={{ px: 2 }}
      >
        <Stack direction='column' spacing={1}>
          <PropertiesLabel i18nkey='interface:properties.template.aisles'>
            <NumberInput
              value={newGenerationParams.numAisles}
              onChange={onNumAislesChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              minValue={1}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.bays'>
            <NumberInput
              value={newGenerationParams.numBays}
              onChange={onNumBaysChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              minValue={1}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.shelves'>
            <NumberInput
              value={newGenerationParams.numShelves}
              onChange={onNumShelvesChange}
              minValue={1}
              disabled={mode !== WorkspaceMode.EDITABLE}
            />
          </PropertiesLabel>
        </Stack>
      </Accordion>
    </Stack>
  );
}

export const LassieMiniTemplateParameters = memo(LassieMiniTemplateParametersComponent);
